---
title: "Support and Resistance: Drawing Levels That Matter"
slug: support-and-resistance
shortTitle: "Support & Resistance"
description: "How to find the support and resistance zones traders actually watch, judge how strong they are, handle breaks and retests, and turn a level into a plan with a defined risk."
level: intermediate
category: trading
course: intermediate
order: 1
topics: [technical-analysis]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/support-and-resistance.svg
faqs:
  - q: "Is support a single price or a zone?"
    a: "Treat it as a zone. Prices rarely turn at exactly the same number twice, so mark a band that covers the wicks and bodies where the market reacted rather than one perfect line."
  - q: "What makes a support or resistance level stronger?"
    a: "Several clear reactions, a higher timeframe such as 4h or 1D, sharp moves away from the level, recent relevance and heavy volume at the turn all add weight. A level touched once on a 1-minute chart carries little."
  - q: "What happens when support breaks?"
    a: "Broken support often acts as resistance when the price comes back to it, which is called role reversal. It does not always happen, and false breaks, where the price pokes through and then returns, are common."
  - q: "Where should a stop loss go when trading from a level?"
    a: "Usually beyond the zone with some extra room, not exactly at its edge, because obvious levels attract brief spikes. Then size the position so that the distance to the stop equals the amount you are willing to lose."
  - q: "Can I draw levels on the trade chart?"
    a: "The trade chart does not include drawing tools. You can note your levels, set price alerts at them, place pending orders there, or draw them in a separate charting tool and place your orders on the platform."
---

Look at almost any chart and you will see prices that keep turning at roughly the same places. A falling market stops and bounces near a certain price, again and again. A rising market stalls under another. Those areas are **support** (a floor where buying has tended to appear) and **resistance** (a ceiling where selling has tended to appear).

They are probably the most widely used idea in technical analysis, and also among the easiest to overdo. This lesson shows you how to find the levels that matter, judge their strength, handle breaks and false breaks, and turn a level into a trade plan with a risk you set in USDT.

<CTA label="Open the GOLD/USDT chart" to="/trade/GOLD" />

<KeyTakeaways>

- **Levels are zones, not exact prices.** Mark a band where the price reacted, not a single perfect line.
- **Fewer, stronger levels beat many weak ones.** Start on higher timeframes and keep only levels with clear, repeated reactions.
- **Broken levels often switch roles.** Old resistance can act as support after a break, but false breaks are common.
- **A level is a place to plan, not a prediction.** Use it to set your entry, stop loss and target with a defined risk.

</KeyTakeaways>

## Why support and resistance form

Levels are not magic. They form because markets have memory and orders cluster:

- **Memory.** Traders who missed a bounce at a price want another chance there. Traders who bought at a peak and are losing may sell when the price returns to their entry.
- **Clustered orders.** Limit orders, stop losses and take profits tend to gather around the same visible prices.
- **Round numbers.** Prices such as 60,000 for Bitcoin or 2,400 for gold attract attention and orders.

Because so many people watch the same areas, reactions there become partly self-fulfilling. But a level is an area of interest, not a wall. Every level eventually breaks.

## How to find levels that matter

<Steps>
<Step title="Start on a higher timeframe">

Open the 1D chart, then the 4h. Levels visible there are watched by more traders than those on a 5-minute chart. See [Reading a Candlestick Chart](/academy/article/reading-candlestick-charts) if timeframes are new.

</Step>
<Step title="Find the obvious turning points">

Look for swing highs and swing lows where the price reversed sharply. If you have to squint to see a level, it probably is not one.

</Step>
<Step title="Look for repeat reactions">

A price area that turned the market two, three or more times is more meaningful than a single touch.

</Step>
<Step title="Mark a zone, not a line">

Cover the cluster of wicks and candle bodies around the turns. The OHLC legend gives you the exact highs and lows.

</Step>
<Step title="Keep only the nearest few">

Two or three levels above the price and two or three below are plenty. Delete the rest.

</Step>
</Steps>

## How strong is a level?

<CompareTable caption="What makes a level stronger or weaker">

| Factor | Stronger | Weaker |
| --- | --- | --- |
| Reactions | Several clear turns | A single touch |
| Timeframe | 4h or 1D | 1m or 5m |
| Move away | Sharp and large | A small drift |
| Age | Recent and still respected | Old and ignored since |
| Volume | Heavy volume at the turn | Thin volume |

</CompareTable>

One nuance: a level tested many times in quick succession can weaken, because the orders sitting there get used up with each visit. Repeated hits in a short time can be a sign that a break is building rather than that the level is unbreakable.

## Breaks, retests and false breaks

When the price moves decisively through a level, the level often swaps roles. Broken resistance becomes support; broken support becomes resistance. This is called **role reversal**, and the return to the old level is called a **retest**.

<Example title="Role reversal on gold (illustrative prices)">

GOLD/USDT turns lower three times from a resistance zone of 2,395–2,405. Then it breaks through and a 4h candle closes at 2,430. Two days later it pulls back to 2,405, finds buyers and rises again. The old ceiling has acted as a floor.

</Example>

Not every break holds. In a **false break**, the price pokes through a level, triggers orders beyond it, then closes back inside the range. Many traders wait for a candle to *close* beyond the zone on their chosen timeframe before treating a break as real. That reduces false signals, but it does not remove them.

## Turning a level into a trade plan

A level gives you three useful prices: where to enter, where you are wrong, and where the next obstacle sits. Here is how the numbers fit together.

<Example title="Planning a buy at support (illustrative prices)">

Gold is trading at 2,415. You have marked a support zone at **2,380–2,390** and resistance near **2,440**. Your risk budget for the trade is **30 USDT**.

- **Entry:** a Buy Limit at **2,392**, just above the zone.
- **Stop loss:** below the zone with room, at **2,372**. Risk per ounce: 2,392 − 2,372 = **20**.
- **Take profit:** just under resistance, at **2,436**. Reward per ounce: 2,436 − 2,392 = **44**.
- **Reward to risk:** 44 ÷ 20 = **2.2**.
- **Size:** 30 ÷ 20 = **1.5 oz**. As an Amount that is 1.5 × 2,392 = **3,588 USDT**; in lots it is 1.5 ÷ 100 = 0.015, since one lot of gold is 100 oz.
- **Outcomes:** the stop costs 1.5 × 20 = **30 USDT**; the target pays 1.5 × 44 = **66 USDT**, both before costs.

</Example>

On {{BRAND}}, open the **Limit / Stop** tab, choose **Buy** and enter 2,392. Because it is below the Ask, the panel labels it a **Buy Limit**. Switch on **TP / SL** and enter your prices, or type 30 into **Loss (USDT)**. Check **Value of $1 move** reads about 1.5, and in Lots mode check the **Min / step** line, since lot sizes are rounded to each market's step. Remember that a pending order reserves margin as soon as you place it. For the math behind the 2.2 figure, see [Risk/Reward Ratio and Win Rate](/academy/article/risk-reward-and-win-rate).

<Warning>

Obvious levels attract brief spikes, so a stop placed exactly at the edge of a zone is easy to hit. A stop loss also fills at market once triggered, so in a fast market or a gap it can close at a worse price than you set. Levels fail regularly: size every trade so that a loss is affordable.

</Warning>

## Marking levels on {{BRAND}}

The trade chart on {{BRAND}} does not currently include drawing tools, so you need another way to keep track of your levels:

- **Price alerts.** Use the bell on a market row, or Account → Alerts, to be told when the price rises to or falls to a level. A range-break alert can tell you when a market trades above its previous 24-hour or 7-day high, or below its low.
- **Orders as markers.** Pending orders, take profits and stop losses appear as lines on the chart, and on desktop you can drag them to a new price.
- **A separate charting tool.** Many traders draw zones in a dedicated charting tool and place their orders on {{BRAND}}.

{/* TODO: verify with platform — the trade chart has no drawing tools or indicators; confirm wording about using a separate charting tool. */}

<ProTip>

Set alerts at the edges of your zones instead of watching every tick. When an alert fires, open the chart and wait for the candle to close before deciding. It turns a reaction into a decision.

</ProTip>

Levels are even more useful when combined with trend direction, covered next in [Trendlines, Channels and Market Structure](/academy/article/trendlines-channels-market-structure), and with [Multi-Timeframe Analysis](/academy/article/multi-timeframe-analysis).

## Conclusion

Support and resistance are areas where buyers or sellers have shown up before and may show up again. Find them on higher timeframes, mark them as zones, keep only the strongest few, and expect broken levels to switch roles, sometimes. Most of all, use a level to plan rather than to predict: it tells you where to enter, where your idea is wrong and where the next obstacle sits, so you can size the trade to a loss you have chosen in advance.
